import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { compact } from 'lodash';
import { CloseGuard, DialogRef, overlayConfigFactory } from 'i2g-ngx-modialog';
import { Modal, VEXModalContext } from 'i2g-ngx-modialog/plugins/vex';
import { Subject } from 'rxjs';
import { IIcon } from '../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../shared/components/icon/icons-sizes.enum';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingCanvasActionEnum,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../tracking/tracking.data';
import { TrackingDispatcher } from '../tracking/tracking.dispatcher';
import { DialogActionsEnum, IDialogData, IDialogTrigger } from './dialog.data';
import { DialogService } from './dialog.service';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-dialog',
  templateUrl: 'dialog.component.html',
  styleUrls: ['dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogComponent implements OnInit, OnDestroy, CloseGuard {
  @Input() headerLabel: string;
  @Input() hideCloseButton: boolean;
  @Input() hideActions: boolean;
  @Input() isPrimaryActionDestroying: boolean;
  @Input() isSecondaryActionDestroying: boolean;
  @Input() actionLeadText: string;
  @Input() htmlActionLead: boolean;
  @Input() footerText: string;
  @Input() htmlFooter: boolean;
  @Input() primaryActionDisabled: boolean;
  @Input() secondaryActionDisabled: boolean;
  @Input() primaryActionText: string;
  @Input() secondaryActionText: string;
  @Input() htmlPrimaryAction: boolean;
  @Input() htmlSecondaryAction: boolean;
  @Input() preventCloseOnEsc: boolean;
  @Input() customClass: string;
  @Input() dialogType: string;
  @Input() centerActions: boolean;
  @Input() stackActions: boolean;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAdditionalProperties: any;
  @Input() trackingDialogName: TrackingDialogNamesEnum;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() primaryActionTapped: ITrackingAction;
  @Input() secondaryActionTapped: ITrackingAction;
  @Input() dismissActionTapped: ITrackingAction;
  @Input() dialogE2eId: string;

  // FIXME: temp fix for "V2E-6083: Add Multiple screen is locked and shifted up when selecting any item at the bottom
  // list" Adds 'overflow: initial' to <modal-overlay> element for item/expense-picker dialogs. This is iterim fix, as
  // it prevents selecting items/expenses near the bottom when browser screen is short.
  @Input() overflowHack = false;

  @Input()
  set primaryActionTrackingAdditionalProperties(
    properties: ITrackingProperties
  ) {
    this._primaryActionTrackingAdditionalProperties = this._getActionTrackingAdditionalProperties(
      properties
    );
  }

  get primaryActionTrackingAdditionalProperties(): ITrackingProperties {
    return this._primaryActionTrackingAdditionalProperties;
  }

  @Input()
  set secondaryActionTrackingAdditionalProperties(
    properties: ITrackingProperties
  ) {
    this._secondaryActionTrackingAdditionalProperties = this._getActionTrackingAdditionalProperties(
      properties
    );
  }

  get secondaryActionTrackingAdditionalProperties(): ITrackingProperties {
    return this._secondaryActionTrackingAdditionalProperties;
  }

  @Input()
  set dismissActionTrackingAdditionalProperties(
    properties: ITrackingProperties
  ) {
    this._dismissActionTrackingAdditionalProperties = this._getActionTrackingAdditionalProperties(
      properties
    );
  }

  get dismissActionTrackingAdditionalProperties(): ITrackingProperties {
    return this._dismissActionTrackingAdditionalProperties;
  }

  @Output() onPrimaryAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSecondaryAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() onCloseAction: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDialogError: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDialogOpen: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDialogClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDialogDestroy: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDismissAction: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('templateRef', { static: true }) templateRef: TemplateRef<any>;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _dialog: DialogRef<any>;
  private _primaryActionTrackingAdditionalProperties: ITrackingProperties;
  private _secondaryActionTrackingAdditionalProperties: ITrackingProperties;
  private _dismissActionTrackingAdditionalProperties: ITrackingProperties;

  closeIcon: IIcon;
  defaultTrackingObject: ITrackingObject;
  dialogPresented: ITrackingAction;
  defaultCta1Tapped: ITrackingAction;
  defaultCta2Tapped: ITrackingAction;
  defaultDismissTapped: ITrackingAction;

  constructor(
    private _modal: Modal,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dialogService: DialogService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnInit() {
    this.closeIcon = {
      name: 'close-navigation',
      size: IconSizesEnum.medium,
    };

    if (this.trackingDialogName) {
      this.defaultTrackingObject = this._trackingObjectBuilder.buildDialog(
        this.trackingDialogName
      );
    }

    const dialogTriggers$ = this._dialogService.dialogTriggers$.pipe(
      takeUntil(this._destroyed),
      filter(
        (dialogTrigger: IDialogTrigger) =>
          dialogTrigger.type === this.dialogType
      )
    );

    dialogTriggers$
      .pipe(
        filter(
          (dialogTrigger: IDialogTrigger) =>
            dialogTrigger.action === DialogActionsEnum.close
        )
      )
      .subscribe(() => this.close());

    dialogTriggers$
      .pipe(
        distinctUntilChanged((a, b) => a && b && a.action === b.action), // prevent open dialog twice
        filter(
          (dialogTrigger: IDialogTrigger) =>
            dialogTrigger.action === DialogActionsEnum.open
        )
      )
      .subscribe(() => {
        if (this.trackingScreenName) {
          if (this.trackingObject || this.defaultTrackingObject) {
            this.dialogPresented = this._trackingActionBuilder.buildPresented(
              this.trackingScreenName
            );
          }

          if (this.primaryActionText && !this.primaryActionTapped) {
            this.defaultCta1Tapped = this._trackingActionBuilder.buildButtonTapped(
              TrackingCanvasActionEnum.cta1,
              this.trackingScreenName
            );
          }

          if (this.secondaryActionText && !this.secondaryActionTapped) {
            this.defaultCta2Tapped = this._trackingActionBuilder.buildButtonTapped(
              TrackingCanvasActionEnum.cta2,
              this.trackingScreenName
            );
          }

          if (!this.dismissActionTapped) {
            this.defaultDismissTapped = this._trackingActionBuilder.buildButtonTapped(
              TrackingCanvasActionEnum.dismiss,
              this.trackingScreenName
            );
          }
        }

        this.open();
      });
  }

  open(): Promise<any> {
    this._dialog = this._modal.open(
      this.templateRef,
      overlayConfigFactory(
        {
          isBlocking: true,
          keyboard: this.preventCloseOnEsc ? null : 27,
          overlayClassName: 'vex-overlay',
          className: compact([
            'default',
            this.overflowHack ? 'overflow-hack' : null,
          ]).join(' '),
          contentClassName: compact([
            'vex-content',
            `${this.dialogType}-dialog`,
            this.customClass || null,
          ]).join(' '),
        },
        VEXModalContext
      )
    );

    if (this._dialog) {
      this.onDialogOpen.emit();

      const trackingObject = this.trackingObject || this.defaultTrackingObject;

      if (trackingObject && this.dialogPresented) {
        this._trackingDispatcher.trackEvent(
          trackingObject,
          this.dialogPresented,
          this.trackingAdditionalProperties
        );
      }

      this._dialog.onDestroy
        .pipe(take(1))
        .subscribe(() => this.onDialogDestroy.emit());

      // handle DialogRef.dismiss() from ngx-modialog: ESC or outside click.
      this._dialog.result.catch(() => this.dismissAction());

      this._changeDetectorRef.markForCheck();

      return this._dialog.result;
    } else {
      return new Promise((_, reject) => reject(null));
    }
  }

  close(emitEvent = true) {
    if (this._dialog) {
      if (emitEvent) {
        this.onDialogClose.emit();
      }

      this._dialog.close();
    }
  }

  beforeDismiss() {
    return true;
  }

  beforeClose() {
    return true;
  }

  primaryAction() {
    this.onPrimaryAction.emit();

    this._dialogService.closeDialog({
      type: this.dialogType,
      data: { result: true } as IDialogData,
    });
  }

  secondaryAction() {
    this.onSecondaryAction.emit();

    this._dialogService.closeDialog({
      type: this.dialogType,
      data: { result: false } as IDialogData,
    });
  }

  closeAction() {
    this.onCloseAction.emit();

    this._dialogService.closeDialog({
      type: this.dialogType,
      data: { result: null } as IDialogData,
    });
  }

  dismissAction() {
    this.onDismissAction.emit();

    this._dialogService.closeDialog({
      type: this.dialogType,
      data: { result: null } as IDialogData,
    });
  }

  ngOnDestroy() {
    if (this._dialog) {
      this._dialog.close();
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  /**
   * Combine the properties with the general trackingAdditionalProperties if exist
   */
  private _getActionTrackingAdditionalProperties(
    properties: ITrackingProperties
  ) {
    return this.trackingAdditionalProperties
      ? { ...this.trackingAdditionalProperties, ...properties }
      : properties;
  }
}
